/**
 * Plan 20 P20.2 (lane B): large off-bridge structures — pier trestles + per-theme shoreline
 * silhouettes standing in the water beside the bridge. Pure geometry as a function of the ring
 * slot index (hashValue, no RNG — today's `propPlacements(index)` input semantics), returned as
 * merge-ready parts the caller merges into the segment's existing concrete mesh (colour attribute
 * present, no material → 0 added draw calls, 0 loaded assets, vertex tints only).
 *
 * Placement geometry (measured against the chase camera at y 4.1 looking down the +z rail, and the
 * continuous deck+shoulder slab y ∈ [-1.1, 0] × |x| ≤ 7.3): a below-deck point at (x, y) is
 * occluded by that slab whenever |x| < 1.78·(4.1−y), so NOTHING inside |x| < 7.3 is built at all —
 * every structure vertex stays beyond the shoulder band (|x| > SHOULDER_OUT) and the trestle caps
 * tuck under the deck edge from outside. Trestle tops stay below deck height; only the per-theme
 * shoreline silhouettes (wreck masts / silo gantries / ruin towers) break the deck line, which the
 * plan permits beyond the shoulder band and which the fire-line rule demands (rays from the camera
 * to the lanes or the |x| ≈ 6.4 braziers never reach |x| ≥ 7.3, so nothing here can occlude them).
 * Shoreline pieces stand in the water (bases y −43.2, hidden under the opaque water plane at −42).
 */
import { Color3, MeshBuilder } from '@babylonjs/core/pure';
import type { Mesh, Scene } from '@babylonjs/core/pure';
import { hashValue } from './scene';
import type { EnvTheme } from './scene';
import { SHOULDER_OUT, tint } from './dressing';

/** Structures live beyond the shoulder band: every vertex keeps |x| > SHOULDER_OUT (7.3). */
export const STRUCTURE_IN = SHOULDER_OUT + 0.05; // trestle cap inner face — 0.05 off the shoulder face (reads as contact)
const TRESTLE_BASE_MIN = 8.3;
const TRESTLE_BASE_SPAN = 0.9; // outboard leg |x| ∈ [8.3, 9.2]; cap outer face ≤ 10.6
const SHORE_MIN = 13.5; // shoreline placement |x| ∈ [13.5, 23.5] — ≥ 0.4 clear of the trestle band
const SHORE_SPAN = 10;
const WATER_BOTTOM = -43.2; // structure bases (the water plane at −42 hides the last 1.2 m)

export type ShoreKind = 'wreck' | 'silo' | 'ruin';

export interface TrestlePlacement {
  side: number; // −1 | 1
  z: number; // centre, segment-local z
  base: number; // signed |x| of the outboard leg
}

export interface ShorePlacement {
  kind: ShoreKind; // per theme: overcast → wreck, industrial → silo, dusk → ruin
  side: number; // −1 | 1
  x: number; // signed centre, world X
  z: number; // centre, segment-local z
  s: number; // scale/variation stream ∈ [0, 1)
}

const SEED_TRESTLE = 0x1a5;
const SEED_TRESTLE_J = 0x2b7;
const SEED_SHORE = 0x3c9;
const SEED_SHORE_J = 0x4db;

const SHORE_KIND: Record<EnvTheme, ShoreKind> = { overcast: 'wreck', industrial: 'silo', dusk: 'ruin' };

/**
 * Pier-trestle bents for a segment: two z slots per side (clear of the pillars at z 20 and the
 * truss at z 40), hash-gated (~78% built) and hash-jittered, keyed by the ring slot index like
 * propPlacements. The cap's inner face tucks under the deck edge at |x| 7.35 (below deck height);
 * the legs run full depth into the water (the water plane hides their feet).
 */
export function trestlePlacements(index: number): TrestlePlacement[] {
  const out: TrestlePlacement[] = [];
  for (const side of [-1, 1]) {
    for (let k = 0; k < 2; k++) {
      const slot = k * 2 + (side > 0 ? 1 : 0);
      if (hashValue(index, slot, SEED_TRESTLE) < 0.22) continue;
      out.push({
        side,
        z: (k === 0 ? 13 : 30) + (hashValue(index, slot + 8, SEED_TRESTLE_J) - 0.5) * 2.8,
        base: side * (TRESTLE_BASE_MIN + hashValue(index, slot + 16, SEED_TRESTLE_J) * TRESTLE_BASE_SPAN),
      });
    }
  }
  return out;
}

/**
 * Shoreline silhouette slots: two z candidates per side (clear of the trestle z bands), hash-gated
 * (~62% built), |x| ∈ [13.5, 23.5] so no vertex can reach the trestle band. The kind is per theme.
 */
export function shorePlacements(index: number, theme: EnvTheme): ShorePlacement[] {
  const out: ShorePlacement[] = [];
  const kind = SHORE_KIND[theme];
  for (const side of [-1, 1]) {
    for (let k = 0; k < 2; k++) {
      const slot = k * 2 + (side > 0 ? 1 : 0);
      if (hashValue(index, slot, SEED_SHORE) < 0.38) continue;
      out.push({
        kind,
        side,
        x: side * (SHORE_MIN + hashValue(index, slot + 8, SEED_SHORE_J) * SHORE_SPAN),
        z: (k === 0 ? 6 : 26) + (hashValue(index, slot + 16, SEED_SHORE_J) - 0.5) * 3.6,
        s: hashValue(index, slot + 24, SEED_SHORE_J),
      });
    }
  }
  return out;
}

// Per-theme vertex tints on the one concrete material (no emissive anywhere: the L3 "fire-lit"
// read is the warm albedo tint under the dusk grade, per the plan).
const TRESTLE_TIMBER = new Color3(0.3, 0.25, 0.2); // overcast: weathered wet timber
const TRESTLE_RUST = new Color3(0.28, 0.19, 0.13); // industrial: rusted steel
const TRESTLE_CHAR = new Color3(0.12, 0.1, 0.09); // dusk: charred
const HULL = new Color3(0.22, 0.17, 0.14);
const HULL_HOUSE = new Color3(0.3, 0.2, 0.13);
const MAST = new Color3(0.15, 0.13, 0.11);
const SILO_CONCRETE = new Color3(0.44, 0.44, 0.42);
const GANTRY_STEEL = new Color3(0.3, 0.3, 0.32);
const GANTRY_RUST = new Color3(0.34, 0.24, 0.16);
const SLAB_FIRELIT = new Color3(0.4, 0.26, 0.17); // the fire-lit read: warm unlit concrete
const SLAB_DIM = new Color3(0.34, 0.22, 0.15);
const SLAB_FALLEN = new Color3(0.28, 0.19, 0.13);
const SLAB_LINTEL = new Color3(0.42, 0.28, 0.18);

function sBox(scene: Scene, parts: Mesh[], index: number, name: string, w: number, h: number, d: number, x: number, y: number, z: number, c: Color3, rotX = 0, rotY = 0, rotZ = 0): Mesh {
  const m = MeshBuilder.CreateBox(`${name}-${index}`, { width: w, height: h, depth: d }, scene);
  m.position.set(x, y, z);
  m.rotation.set(rotX, rotY, rotZ);
  parts.push(tint(m, c));
  return m;
}

function sCyl(scene: Scene, parts: Mesh[], index: number, name: string, dia: number, h: number, x: number, y: number, z: number, c: Color3, tess = 6, rotX = 0, diaTop?: number): Mesh {
  const m = MeshBuilder.CreateCylinder(`${name}-${index}`, { diameter: dia, height: h, tessellation: tess, diameterTop: diaTop }, scene);
  m.position.set(x, y, z);
  m.rotation.x = rotX;
  parts.push(tint(m, c));
  return m;
}

const trestleTint = (theme: EnvTheme): Color3 => (theme === 'industrial' ? TRESTLE_RUST : theme === 'dusk' ? TRESTLE_CHAR : TRESTLE_TIMBER);

/**
 * All structure parts for one segment slot (pure in index + theme). Every part is tinted and
 * material-free — the caller merges them into the segment's concrete mesh exactly like the
 * dressing parts (world.ts), so the whole feature costs 0 added draw calls on every tier.
 */
export function buildStructures(scene: Scene, index: number, theme: EnvTheme): Mesh[] {
  const parts: Mesh[] = [];
  const box = sBox.bind(null, scene, parts, index);
  const cyl = sCyl.bind(null, scene, parts, index);

  // Pier trestles: cap beam tucked under the deck edge + two full-depth legs + knee braces.
  const tTint = trestleTint(theme);
  for (const t of trestlePlacements(index)) {
    const a = Math.abs(t.base);
    const capOut = a + 1.4;
    box('trestle-cap', capOut - STRUCTURE_IN, 0.45, 2.6, t.side * ((STRUCTURE_IN + capOut) / 2), -0.72, t.z, tTint);
    for (const dz of [-1.05, 1.05]) {
      cyl('trestle-leg', 0.42, -0.85 - WATER_BOTTOM, t.base, (-0.85 + WATER_BOTTOM) / 2, t.z + dz, tTint);
      // Knee brace: cap outboard end down-inboard past the leg (x-y plane, at the leg's z).
      const len = Math.hypot(1.65, 3.6);
      box('trestle-brace', 0.15, len, 0.15, t.side * (a + 0.275), -2.6, t.z + dz, tTint, 0, 0, -t.side * Math.atan2(1.65, 3.6));
    }
  }

  // Per-theme shoreline silhouettes standing in the water beside the bridge.
  for (const p of shorePlacements(index, theme)) {
    switch (p.kind) {
      case 'wreck': {
        // Half-sunken hull + deckhouse + vertical mast breaking the deck line + drifted boom.
        const w = 2.4 + 1.2 * p.s;
        const d = 4.5 + 1.5 * p.s;
        const tilt = p.side * (0.08 + 0.1 * p.s);
        box('wreck-hull', w, 1.5, d, p.x, -41.35, p.z, HULL, (p.s - 0.5) * 0.1, 0, tilt);
        box('wreck-house', w * 0.45, 1.1, d * 0.32, p.x, -40.3, p.z + d * 0.18, HULL_HOUSE, 0, 0, tilt);
        cyl('wreck-mast', 0.18, 41 + 2.2 * p.s, p.x, (-40.2 + 0.8 + 2.2 * p.s) / 2, p.z + d * 0.1, MAST);
        box('wreck-boom', 0.14, 0.14, d * 0.5, p.x + p.side * (w / 2 + 0.2), -41.7, p.z - d * 0.15, MAST);
        break;
      }
      case 'silo': {
        // Two tapered silos + a tall outboard gantry: columns, top beam, diagonal brace.
        const dia = 2.6 + 1.0 * p.s;
        const siloTop = -27 - 4 * p.s;
        cyl('silo-body', dia, siloTop - WATER_BOTTOM, p.x, (siloTop + WATER_BOTTOM) / 2, p.z, SILO_CONCRETE, 8, 0, dia * 0.92);
        cyl('silo-body', dia * 0.9, siloTop - WATER_BOTTOM - 1.5, p.x, (siloTop - 1.5 + WATER_BOTTOM) / 2, p.z + 3.4, SILO_CONCRETE, 8, 0, dia * 0.82);
        const gTop = 3 + 5 * p.s;
        const gBottom = siloTop;
        const gH = gTop - gBottom;
        for (const dz of [-0.6, 4.0]) {
          box('silo-col', 0.45, gH, 0.45, p.x + p.side * 1.55, (gTop + gBottom) / 2, p.z + dz, GANTRY_STEEL);
        }
        box('silo-beam', 0.3, 0.42, 4.9, p.x + p.side * 1.55, gTop - 0.25, p.z + 1.7, GANTRY_STEEL);
        box('silo-diag', 0.14, Math.hypot(4.6, 12), 0.14, p.x + p.side * 1.55, gBottom + 8, p.z + 1.7, GANTRY_RUST, Math.atan2(4.6, 12));
        break;
      }
      case 'ruin': {
        // Fire-lit concrete: one tall broken slab (tops the deck line), a lower slab, a fallen
        // slab in the water and a lintel cap — warm tints, no emissive.
        const tw = 1.6 + 0.8 * p.s;
        const tTop = 0.5 + 3.0 * p.s;
        box('ruin-slab', tw, tTop - WATER_BOTTOM, 0.6, p.x, (tTop + WATER_BOTTOM) / 2, p.z, SLAB_FIRELIT, (p.s - 0.5) * 0.06, 0, -p.side * (0.02 + 0.04 * p.s));
        const lw = 2.4 + 1.0 * p.s;
        const lh = 6 + 4 * p.s;
        box('ruin-slab', lw, lh, 0.55, p.x, WATER_BOTTOM + lh / 2, p.z + 2.2, SLAB_DIM, 0, 0, p.side * (0.05 + 0.07 * p.s));
        box('ruin-fallen', 2.2 + 0.8 * p.s, 0.5, 3.2 + 1.2 * p.s, p.x, -41.4, p.z, SLAB_FALLEN, 0.1, 0, p.side * 0.12);
        box('ruin-lintel', tw * 0.8, 0.4, 0.5, p.x + p.side * 0.3, tTop - 0.5, p.z, SLAB_LINTEL);
        break;
      }
    }
  }
  return parts;
}
